import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../../../../../contexts/AuthContext';
import { auth } from '../../../../../config/firebase';
import { clearServerDocsCopies, setServerDocsCopyStore } from '../useDataSource';
import { createIdbBackend } from './idbBackend';
import { createPersistentServerDocsCopyStore } from './persistentCopyStore';

const currentUid = (): string => auth?.currentUser?.uid ?? '';

let sharedBackend: ReturnType<typeof createIdbBackend> | undefined;
const backend = () => (sharedBackend ??= createIdbBackend());

/** A wipe that could not delete leaves the previous user's customer list on a shared device: that is an error, not a warning. */
function reportWipeFailure(error: unknown): void {
  Sentry.captureException(error, { tags: { area: 'serverDocsCopy', op: 'wipe' }, level: 'error' });
}

/**
 * Delete every persisted server-docs copy, for every user. Registered as the main app's offline-cache clearer, so it runs on logout and when a
 * different user signs in on the same device (firebase-auth.ts clearOfflineCache), before the page reloads. Never throws.
 */
export function wipePersistedServerDocsCopies(): Promise<void> {
  return createPersistentServerDocsCopyStore({ backend: backend(), getUid: currentUid, buster: '', onWipeFailure: reportWipeFailure }).wipeAll();
}

/**
 * Gives the phone's copy of the generators list a place on the device (OFFICEHUB-CLIENT-9C), for signed-in STAFF only: the list names every
 * customer's sites, so an external contractor's or investor's browser must not hold it. A user is staff only when the profile read is the
 * signed-in account's own (the pairing TechGeneratorsOfflineCopy uses) and the profile is not external.
 * - A staff user registers the store and removes every other user's copies from the device.
 * - A user KNOWN to be external (or an Investor) has any copy removed. A signed-out or still-loading user changes nothing: a page reload passes through
 *   "nobody yet", and wiping then would defeat the copy.
 * - Sign-out and user switch wipe through clearOfflineCache (see wipePersistedServerDocsCopies).
 */
export function ServerDocsCopyRegistrar({ buster, onError }: { buster: string; onError?: (error: unknown) => void }): null {
  const { user, firebaseUser, profileUid } = useAuth();
  const uid = firebaseUser?.uid;
  const profileIsOwn = !!user && !!uid && profileUid === uid;
  // An Investor is not external by flag but is not staff either: the list names every customer's sites.
  const notStaff = profileIsOwn && (user.isExternal === true || user.role === 'Investor');
  const isStaff = profileIsOwn && !notStaff;
  const knownExternal = notStaff;

  useEffect(() => {
    if (knownExternal) void wipePersistedServerDocsCopies();
  }, [knownExternal]);

  useEffect(() => {
    if (!isStaff || !uid) return undefined;
    const store = createPersistentServerDocsCopyStore({ backend: backend(), getUid: currentUid, buster, onError, onWipeFailure: reportWipeFailure });
    void store.wipeOthers(uid);
    setServerDocsCopyStore(store);
    return () => {
      store.dispose(); // a save still in flight takes itself back; the wipe that may follow cannot be undone by a late write
      setServerDocsCopyStore(null);
      clearServerDocsCopies(); // the rows leave tab memory with the user that held them
    };
  }, [isStaff, uid, buster, onError]);

  return null;
}
